import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { api } from '../../api/client'
import { describeError } from '../../lib/describeError'
import type { PieceDetail } from '../api'
import { SAVE_FIRST } from './pieceHelpers'
import { captionPhoto, isTextChanged, openSuggestions, suggestionText, TEXT_CHANGED, type Suggestion } from './suggestions'
import { btnSmall, errorText } from './ui'

const SD = '/api/lab/section-drafts'

type Props = {
  detail: PieceDetail
  editable: boolean
  /** Saves what waits first; false refuses the write. */
  beforeWrite: () => Promise<boolean>
  /** The page adopts the copy the server returns (an accept changes the body). */
  onDetail: (r: PieceDetail) => void
}

export function SuggestionsTab({ detail, editable, beforeWrite, onDetail }: Props) {
  const open = openSuggestions(detail)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const act = useMutation({
    mutationFn: async ({ s, verb }: { s: Suggestion; verb: 'accept' | 'dismiss' }) => {
      if (!(await beforeWrite())) throw new Error(SAVE_FIRST)
      return api.post<PieceDetail>(`${SD}/${detail.id}/suggestions/${encodeURIComponent(s.id)}/${verb}`)
    },
    onMutate: ({ s }) => setErrors((e) => { const rest = { ...e }; delete rest[s.id]; return rest }),
    onSuccess: (r) => onDetail(r),
    onError: (err, { s }) => setErrors((e) => ({ ...e, [s.id]: isTextChanged(err) ? TEXT_CHANGED : describeError(err) })),
  })
  const busy = act.isPending
  return (
    <>
      <h2 className="m-0 mb-1.5 font-ui text-[15px] font-semibold text-paper">Suggestions</h2>
      {open.length === 0 && <p className="m-0 text-[13px] text-paper-dim">No suggestions left.</p>}
      {open.map((s) => {
        const t = suggestionText(s)
        const photo = captionPhoto(s, detail.body_html)
        return (
          <div key={s.id} className="flex flex-col gap-2 border-t border-ink-500 py-3">
            <span className="text-[12px] font-bold uppercase tracking-[0.08em] text-paper-faint">{t.label}</span>
            {s.kind === 'typo' ? (
              <p className="m-0 font-serif text-[15px] leading-snug">
                {s.context.includes(t.before) ? (
                  <>
                    {s.context.slice(0, s.context.indexOf(t.before))}
                    <del className="bg-radar-red/15">{t.before}</del><ins className="bg-radar-green/20 no-underline">{t.after}</ins>
                    {s.context.slice(s.context.indexOf(t.before) + t.before.length)}
                  </>
                ) : <><del className="bg-radar-red/15">{t.before}</del> <ins className="bg-radar-green/20 no-underline">{t.after}</ins></>}
              </p>
            ) : (
              <div className="flex items-start gap-3">
                {photo && <img src={photo} alt="The photo this caption is for" className="h-16 w-24 shrink-0 rounded object-cover" loading="lazy" />}
                <p className="m-0 font-serif text-[15px] leading-snug">{t.after}</p>
              </div>
            )}
            <div className="flex gap-2">
              <button type="button" className={btnSmall} disabled={!editable || busy} onClick={() => act.mutate({ s, verb: 'accept' })}>Accept</button>
              <button type="button" className={btnSmall} disabled={!editable || busy} onClick={() => act.mutate({ s, verb: 'dismiss' })}>Dismiss</button>
            </div>
            {errors[s.id] && <p role="alert" className={errorText}>{errors[s.id]}</p>}
          </div>
        )
      })}
      {!editable && open.length > 0 && <p className="m-0 mt-2 text-[12px] text-paper-faint">These can't change on this piece.</p>}
    </>
  )
}
